import React, {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  ConfigProvider,
  Dropdown,
  Row,
  Select,
  Table,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { debouncedDataLoadWaitMs, ORDER_TYPES } from 'helpers/constants';
import { CSVLink } from 'react-csv';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import {
  getExcelUrlStoreCategoryRankingTemplate,
  getStoreCategoryRankings,
} from '../../../../store/requests/definitions';
import ImportStoreBasedSection from './ImportStoreBasedSection';
import { FILE_TEMPLATE_CODE } from 'helpers/constants';

const { Option } = Select;
let lastQuery = null;

const StoreBasedSectionSorting = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const csvLinkRef = useRef();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);  
  const activeStore = useSelector((state) => state.auth?.user?.activeStore);
  const activeRegionID = useSelector(
    (state) => state.auth?.user?.activeRegion?.id,
  );
  const [selectedStore, setSelectedStore] = useState([]);
  const [stores, setStores] = useState([]);
  const userRegions = useSelector((state) => state?.definitions?.userRegions);
  const [csvDataInfo, setCsvDataInfo] = useState({
    totalElements: null,
    data: [],
    loading: false,
  });

  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });  

  useEffect(() => {
    let allStoresSelectedRegions = [];

    if (activeRegionID) {
      userRegions.forEach((userRegion) => {
        if (userRegion.id === activeRegionID) {
          allStoresSelectedRegions.push(...userRegion.children);
        }
      });
      setStores(allStoresSelectedRegions);
    }
    if (activeStore) {
      setSelectedStore([activeStore.code]);
    }
  }, [userRegions, activeRegionID, activeStore]);

  useEffect(() => {
    if (selectedStore?.length > 0) {
      fetchData();
      return () => {
        setLoading(false);
      };
    }
  }, [
    tableParams?.pagination?.current,
    tableParams?.pagination?.pageSize,
    tableParams?.column,
    selectedStore,
  ]);

  const fetchData = () => {
    if (selectedStore?.length > 0) {
      const payload = getRequestBodyAndString();
      loadData(payload);
    }
  };

  const getRequestBodyAndString = () => {
    let payload = {
      storeCodes: selectedStore,
      queryString: '',
      filter: '',
      ascendingOrder: false,
    };
    payload.queryString = `${payload.queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    if (tableParams.column) {
      payload = {
        ...payload,
        filter: tableParams.column.dataIndex,
        ascendingOrder: ORDER_TYPES[tableParams.order] === ORDER_TYPES.ascend,
      };
    }

    return payload;
  };

  const getAllStoreBasedSectionsForDownload = () => {
    setCsvDataInfo((prev) => ({ ...prev, data: [], loading: true }));
    const payload = {
      storeCodes: selectedStore,
      queryString: `&size=${csvDataInfo.totalElements}`,
      filter: '',
      ascendingOrder: false,
    };
    getStoreCategoryRankings(payload)
      .then((res) => {
        const { content } = res;
        const csvData = [columns.map((col) => col.title)];
        content.forEach((item) => {
          const row = columns.map((col) => item[col.dataIndex]);
          csvData.push(row);
        });
        setCsvDataInfo((prev) => ({ ...prev, data: csvData, loading: false }));
        csvLinkRef.current.link.click();
      })
      .finally(() => setCsvDataInfo((prev) => ({ ...prev, loading: false })));
  };

  const columns = [
    {
      title: t('storeId'),
      dataIndex: 'storeId',
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: t('storeCode'),
      dataIndex: 'storeCode',
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: t('categoryCode'),
      dataIndex: 'categoryCode',
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: t('categoryName'),
      dataIndex: 'categoryName',
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: t('pickingOrder'),
      dataIndex: 'pickingOrder',
      showSorterTooltip: false,
      sorter: true,
    },
  ];

  const actions = [
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <>
          <Button
            shape="round"
            onClick={getAllStoreBasedSectionsForDownload}
            disabled={!(selectedStore?.length > 0 && rows.length > 0)}
            icon={<DownloadOutlined />}
            loading={csvDataInfo.loading}
          />
          <CSVLink
            filename={t('storeBasedSections')}
            data={csvDataInfo.data}
            ref={csvLinkRef}
            className="hidden"
          />
        </>
      ),
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('import'),
      icon: <UploadOutlined />,
      onClick: () => setIsImportModalOpen(true),
      disabled: false,
      hasDropdown: true,
    },
  ];
  async function loadDataWithDebounce(payload) {
    setRows([]);
    setLoading(true);
    getStoreCategoryRankings(payload)
      .then((res) => {
        const { content, page } = res;
        setRows(content);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: page.totalElements,
          },
        });
        setCsvDataInfo((prev) => ({
          data: [],
          totalElements: page.totalElements,
          loading: false,
        }));
      })
      .finally(() => setLoading(false));
  }

  const loadData = useCallback(
    debounce(
      (payload) => {
        loadDataWithDebounce(payload);
      },
      debouncedDataLoadWaitMs,
      [selectedStore],
    ),
  );

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      if (item.hasDropdown) {
        return (
          item.isCrudEnabled &&
          (item.customContent ?? (
            <Dropdown
              key={idx}
              menu={{
                items: [
                  {
                    label: t('bulkAddAndUpdate'),
                    icon: <UploadOutlined />,
                    onClick: () => {
                      setIsImportModalOpen(true);
                    },
                  },
                ],
              }}
              placement="bottomRight"
              arrow
              trigger={['click']}
            >
              <Tooltip title={item.title} key={idx}>
                <Button
                  type={item.type}
                  shape="round"
                  icon={item.icon}
                  style={{ marginBottom: '10px', marginRight: '3px' }}
                  disabled={item.disabled}
                  danger={item.danger}
                />
              </Tooltip>
            </Dropdown>
          ))
        );
      } else {
        return (
          <Fragment key={item.id || idx}>
            {item.isCrudEnabled &&
              (item.customContent ?? (
                <Button
                  type={item?.type}
                  shape="round"
                  icon={item?.icon}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  style={{ marginBottom: '10px', marginRight: '3px' }}
                  danger={item?.danger}
                />
              ))}
          </Fragment>
        );
      }
    });
  };

  const handleOnChangeStore = (selected, options) => {
    if (selected.length > 0) {
      setTimeout(() => {
        const selectAllStore = options.find((option) => option.value === 'ALL');
        if (selectAllStore) {
          const allStoresCode = stores.map((store) => store.code);
          setSelectedStore(allStoresCode);
        } else {
          setSelectedStore(selected);
        }
      }, 500);
    } else {
      setSelectedStore([]);
      setRows([]);
      setCsvDataInfo({ totalElements: null, data: [], loading: false });
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
      </Row>
      {activeRegionID && (
        <Select
          className="fullWidth"
          showSearch
          loading={!stores.length > 0}
          filterOption={true}
          optionFilterProp="children"
          placeholder={t('selectStore')}
          style={{ width: '50%', marginRight: '2px' }}
          onChange={handleOnChangeStore}
          maxTagCount={5}
          mode="multiple"
        >
          <Option key={'ALL'} value={'ALL'}>
            {t('selectAll')}
          </Option>
          {stores &&
            stores.map((store) => (
              <Option key={`${store.id}-${store.code}`} value={store.code}>
                {store.title}
              </Option>
            ))}
        </Select>
      )}
      {
        <Row style={{ marginTop: '10px' }}>
          <ConfigProvider locale={trTR}>
            <Table
              style={{ width: '100%' }}
              size="small"
              columns={columns}
              dataSource={rows}
              pagination={tableParams.pagination}
              loading={loading}
              onChange={handleTableChange}
              rowKey={(record, index) => record?.storeId + index}
              scroll={{
                y: '70vh',
              }}
            />
          </ConfigProvider>
        </Row>
      }
      <Suspense fallback={<></>}>
        {isImportModalOpen && (
          <ImportStoreBasedSection
            onCloseHandler={() => setIsImportModalOpen(false)}
            batchExcelUrl={FILE_TEMPLATE_CODE.STORE_CATEGORY_RANKING_CREATE_OR_UPDATE}
            onSuccessCallback={fetchData}
            isCourier={true}
          />
        )}
      </Suspense>
    </div>
  );
};

export default StoreBasedSectionSorting;
